/**
 * Semantic half of profile validation, pure and headless.
 *
 * A profile that is subtly wrong — a door to a room that does not exist, a spawn
 * outside the house, an objective asking for an object nobody placed — produces a game
 * that looks fine and plays broken. Catching it here means the build fails with a named
 * field instead of the player finding out.
 */

import {
  buildGrounds,
  CELL_SIZE,
  DEFAULT_HEDGE_HEIGHT,
  DEFAULT_MOAT_WIDTH,
  groundsRect,
  startCourtyardRect,
} from '../maze/buildGrounds.ts';
import { deadEnds } from '../maze/deadEnds.ts';
import { generateMaze } from '../maze/generateMaze.ts';
import { ENDLESS_ID_PREFIX } from '../endless.ts';
import { parseProfileData } from './parse.ts';
import {
  boxMax,
  boxMin,
  boxTop,
  rectContains,
  rectContainsRect,
  sharedBoundary,
  wallSpan,
  type Box,
  type GroundPoint,
  type GroundsSpec,
  type LevelSpec,
  type Profile,
  type RoomSpec,
  type WindowSpec,
} from './types.ts';

/**
 * The tallest rise the character controller will step onto rather than be blocked by.
 * A walkability property of levels, not a renderer setting — levels are validated
 * without a physics engine present.
 */
export const MAX_STEP_HEIGHT = 0.4;

/**
 * The player capsule's radius, mirroring `PLAYER_RADIUS` in `src/engine/constants.ts`.
 * Like `MAX_STEP_HEIGHT` it is a walkability property of levels rather than a renderer
 * setting: it decides how wide a gap has to be before anyone fits through it, and levels
 * are validated with no renderer present.
 */
export const PLAYER_CAPSULE_RADIUS = 0.3;

export const DEFAULT_CEILING_HEIGHT = 2.6;

/**
 * How high a jumping player's eyes reach: `EYE_HEIGHT` plus the apex of a jump,
 * `JUMP_SPEED**2 / (2 * -GRAVITY)` ≈ 0.9 m. Mirrored from `src/engine/constants.ts` for
 * the reason `MAX_STEP_HEIGHT` and `PLAYER_CAPSULE_RADIUS` are: what a level has to hide
 * is a property of the level, checked with no renderer present. The mirror is held to
 * the real constants by a test.
 */
export const JUMPING_EYE_HEIGHT = 2.6;

/**
 * The window a hedge has to stand in, both bounds exclusive. The maze must keep its
 * layout to itself: below 2.85 m a jump lifts the player's eyes over the crest — which
 * the top lumps dip up to 5% below the box — and one tap of Space reads the solution off
 * the top of the hedges. Above 3.6 m a 2.4 m corridor is a slot, and the overhead
 * moonlight the level is lit by stops reaching its floor.
 */
export const MIN_HEDGE_HEIGHT = 2.85;
export const MAX_HEDGE_HEIGHT = 3.6;

/** Below this a maze has no room to branch, and is a corridor with corners. */
export const MIN_MAZE_CELLS = 4;

/**
 * Above this we have never generated one: 40×40 is the grid `generateMaze` is tested
 * against, and validation itself generates the maze to check the spawn against it — so
 * an unbounded `columns` is not a level that plays badly, it is a build that never
 * finishes.
 */
export const MAX_MAZE_CELLS = 40;

/**
 * Skirting, cornice and casings for a profile that does not name a trim colour. Pale,
 * like the ceilings — very *Hello Neighbor*.
 */
export const DEFAULT_TRIM_COLOR = '#f2ecdd';

function findDuplicates(ids: readonly string[]): string[] {
  const seen = new Set<string>();
  const duplicated = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) duplicated.add(id);
    seen.add(id);
  }
  return [...duplicated];
}

/**
 * A window has more ways to be subtly wrong than most things a profile can say: it can
 * run off the end of its wall, collide with a doorway, or be cut into a wall that turns
 * out to have another room behind it. All three produce a house that renders and plays
 * but is visibly broken, so each is named here rather than found by a player.
 */
function findWindowProblems(
  window: WindowSpec,
  room: RoomSpec,
  level: LevelSpec,
  ceilingHeight: number,
): string[] {
  const problems: string[] = [];
  const name = `window "${window.id}" in room "${room.id}"`;

  if (window.width <= 0) problems.push(`${name}: width must be positive`);
  if (window.sill < 0) problems.push(`${name}: sill must not be below the floor`);
  if (window.head <= window.sill) problems.push(`${name}: head must be above the sill`);
  if (window.head > ceilingHeight) problems.push(`${name}: head exceeds the ceiling`);

  const span = wallSpan(room.rect, window.wall);
  const from = span.from + window.offset - window.width / 2;
  const to = span.from + window.offset + window.width / 2;
  if (from < span.from - 1e-6 || to > span.to + 1e-6) {
    problems.push(`${name}: does not fit within its ${window.wall} wall`);
  }

  for (const door of level.doors) {
    if (!door.betweenRooms.includes(room.id)) continue;
    const doorLine = span.axis === 'x' ? door.center.x : door.center.z;
    if (Math.abs(doorLine - span.line) > 1e-4) continue;

    const along = span.axis === 'x' ? door.center.z : door.center.x;
    if (along - door.width / 2 < to - 1e-6 && along + door.width / 2 > from + 1e-6) {
      problems.push(`${name}: overlaps door "${door.id}"`);
    }
  }

  // An interior wall has another room on its far side, so a window through it would
  // look into that room rather than outside.
  for (const other of level.rooms) {
    if (other.id === room.id) continue;
    const boundary = sharedBoundary(room.rect, other.rect);
    if (!boundary || boundary.axis !== span.axis) continue;
    if (Math.abs(boundary.line - span.line) > 1e-6) continue;
    if (boundary.min < to - 1e-6 && boundary.max > from + 1e-6) {
      problems.push(`${name}: is on an interior wall, shared with room "${other.id}"`);
    }
  }

  return problems;
}

/** Does a box stand over this point on the ground plane? */
function boxCovers(box: Box, point: GroundPoint): boolean {
  const min = boxMin(box);
  const max = boxMax(box);
  return point.x >= min.x && point.x <= max.x && point.z >= min.z && point.z <= max.z;
}

/**
 * A maze level goes wrong in ways a house cannot: a grid too small to branch, a hedge
 * the player can see over from the ground (which is the whole level, given away), or a
 * spawn out in the water or buried in a hedge. None of them fail to render — they just
 * play badly — so each is named here.
 */
function findGroundsProblems(level: LevelSpec, grounds: GroundsSpec): string[] {
  const problems: string[] = [];

  if (level.doors.length > 0) problems.push('grounds levels cannot have doors');
  if ((level.steps ?? []).length > 0) problems.push('grounds levels cannot have steps');
  if (level.rooms.some((room) => (room.windows ?? []).length > 0)) {
    problems.push('grounds levels cannot have windows');
  }

  let hasUsableGrid = true;
  for (const [field, cells] of [
    ['columns', grounds.columns],
    ['rows', grounds.rows],
  ] as const) {
    if (!Number.isInteger(cells)) {
      problems.push(`grounds ${field} must be a whole number of cells`);
      hasUsableGrid = false;
    } else if (cells < MIN_MAZE_CELLS) {
      problems.push(`grounds ${field} must be at least ${MIN_MAZE_CELLS} cells to be a maze`);
      hasUsableGrid = false;
    } else if (cells > MAX_MAZE_CELLS) {
      problems.push(
        `grounds ${field} must be at most ${MAX_MAZE_CELLS} cells — beyond that the maze is ` +
          'untested, and validating one means generating it',
      );
      hasUsableGrid = false;
    }
  }

  const hedgeHeight = grounds.hedgeHeight ?? DEFAULT_HEDGE_HEIGHT;
  if (hedgeHeight <= MIN_HEDGE_HEIGHT || hedgeHeight >= MAX_HEDGE_HEIGHT) {
    problems.push(
      `grounds hedgeHeight must be between ${MIN_HEDGE_HEIGHT}m and ${MAX_HEDGE_HEIGHT}m, ` +
        'so no player can see over one, standing or at the apex of a jump',
    );
  }

  if ((grounds.moatWidth ?? DEFAULT_MOAT_WIDTH) <= 0) {
    problems.push('grounds moatWidth must be positive');
  }

  // At most one ladder per cell, so a bigger count is a request that cannot be met.
  // Silently placing fewer than the profile asked for is the kind of thing nobody
  // notices until someone counts them months later.
  const ladderCount = grounds.ladderCount ?? 0;
  if (!Number.isInteger(ladderCount) || ladderCount < 0) {
    problems.push('grounds ladderCount must be a whole number of ladders, or absent for none');
  } else if (hasUsableGrid && ladderCount > grounds.columns * grounds.rows) {
    problems.push(
      `grounds ladderCount must be at most ${grounds.columns * grounds.rows} — one per cell ` +
        'of the maze, and no two ladders share a cell',
    );
  }

  // Shape only: whether the maze has enough dead ends to stand them in needs the maze
  // itself, and is asked below once it exists.
  const tableCount = grounds.tableCount ?? 0;
  const tableCountIsSane = Number.isInteger(tableCount) && tableCount >= 0;
  if (!tableCountIsSane) {
    problems.push('grounds tableCount must be a whole number of tables, or absent for none');
  }

  // Items lie on table tops, so what can be asked of them is settled by the table count
  // alone — no maze needed. A profile that asks for more items than tables, or for items
  // with no tables at all, is stating something that cannot be built; placing the ones
  // that fit and dropping the rest is exactly the silent shortfall this refuses to take.
  //
  // There is no rule here that an item must be pocketable: the hedge maze now leaves a
  // two-handed thing — the robot dog's body — on a table on purpose (design D10), and
  // `ObjectKind` names only kinds that are carryable at all, so every value the parser
  // accepts already qualifies. The guard that still matters is the count above: one item
  // per table, and never more items than tables to lay them on.
  const tableItems = grounds.tableItems ?? [];
  if (tableItems.length > 0 && tableCountIsSane && tableItems.length > tableCount) {
    problems.push(
      `grounds tableItems has ${tableItems.length} items but only ${tableCount} tables ` +
        'to lay them on — one item per table, and no table holds two',
    );
  }

  // Everything below needs a maze to exist before it can ask anything of it.
  if (!hasUsableGrid) return problems;

  if (!rectContains(groundsRect(grounds.columns, grounds.rows), level.spawn.position)) {
    problems.push('spawn position must be on the grounds, inside the moat');
    return problems;
  }

  const maze = generateMaze(grounds.seed, grounds.columns, grounds.rows);

  // One table per dead end, so a bigger count is a request the maze cannot meet. How many
  // dead ends a maze has depends on its seed, which is why this cannot be a rule about the
  // grid size: a 12x12 has plenty, and the same 12x12 with another seed has fewer.
  if (tableCountIsSane) {
    const corners = deadEnds(maze).length;
    if (tableCount > corners) {
      problems.push(
        `grounds tableCount must be at most ${corners} — the number of dead ends seed ` +
          `"${grounds.seed}" leaves in this maze, and no two tables share one`,
      );
    }
  }

  const { hedges } = buildGrounds(maze, grounds, null);
  if (hedges.some((hedge) => boxCovers(hedge, level.spawn.position))) {
    problems.push('spawn position is inside a hedge');
  }

  // The seed decides which column the entry gap falls in, so a spawn authored by hand
  // drifts onto the hedge beside it the moment the seed changes — and the player starts
  // the level nose-first against a wall of privet. The capsule has to clear both jambs,
  // not merely stand somewhere in the gap.
  const gapFrom = (-grounds.columns * CELL_SIZE) / 2 + maze.entryColumn * CELL_SIZE;
  const spawnX = level.spawn.position.x;
  if (
    spawnX < gapFrom + PLAYER_CAPSULE_RADIUS ||
    spawnX > gapFrom + CELL_SIZE - PLAYER_CAPSULE_RADIUS
  ) {
    problems.push(
      `spawn position must line up with the maze entry gap, which seed "${grounds.seed}" opens ` +
        `between x ${gapFrom} and x ${gapFrom + CELL_SIZE}`,
    );
  }

  // A declared courtyard walls the spawn in, so the spawn had better be inside it. Where the
  // yard stands comes from the seed (it is centred on the entry gap), which is exactly why this
  // cannot be left to the author: the rule above keeps a hand-written spawn in the gap's column,
  // and this one keeps it in the yard built around that column.
  if (grounds.startCourtyard) {
    const yard = startCourtyardRect(grounds.columns, grounds.rows, maze.entryColumn);
    const { x, z } = level.spawn.position;
    const round = (value: number) => Number(value.toFixed(2));
    if (
      x < yard.x + PLAYER_CAPSULE_RADIUS ||
      x > yard.x + yard.width - PLAYER_CAPSULE_RADIUS ||
      z < yard.z + PLAYER_CAPSULE_RADIUS ||
      z > yard.z + yard.depth - PLAYER_CAPSULE_RADIUS
    ) {
      problems.push(
        `spawn position must stand inside the grounds startCourtyard, which seed ` +
          `"${grounds.seed}" walls between x ${round(yard.x)} and x ${round(yard.x + yard.width)} ` +
          `and between z ${round(yard.z)} and z ${round(yard.z + yard.depth)} — a spawn outside ` +
          `it starts the player behind a hedge`,
      );
    }
  }

  return problems;
}

function findLevelProblems(level: LevelSpec): string[] {
  const problems: string[] = [];
  const at = (message: string) => problems.push(`level "${level.id}": ${message}`);

  const roomsById = new Map(level.rooms.map((room) => [room.id, room]));
  const objectIds = new Set(level.objects.map((object) => object.id));
  const ceilingHeight = level.ceilingHeight ?? DEFAULT_CEILING_HEIGHT;

  for (const collection of [
    ['room', level.rooms.map((room) => room.id)],
    ['door', level.doors.map((door) => door.id)],
    ['object', level.objects.map((object) => object.id)],
    ['objective', level.objectives.map((objective) => objective.id)],
  ] as const) {
    const [kind, ids] = collection;
    for (const id of findDuplicates(ids)) at(`duplicate ${kind} id "${id}"`);
  }

  if (ceilingHeight <= 0) at('ceilingHeight must be positive');

  // A level is a house or it is a maze. Both at once would need two spawn rules, two
  // lighting rules and two exteriors; neither leaves the player nowhere to stand.
  if (level.grounds && level.rooms.length > 0) {
    at('declares both grounds and rooms — a level is one or the other');
  } else if (!level.grounds && level.rooms.length === 0) {
    at('declares neither rooms nor grounds — a level must have somewhere to stand');
  }
  if (level.grounds) findGroundsProblems(level, level.grounds).forEach(at);

  for (const room of level.rooms) {
    if (room.rect.width <= 0 || room.rect.depth <= 0) {
      at(`room "${room.id}": rect width and depth must be positive`);
    }

    const light = room.light;
    if (!light) continue;
    const named = `room "${room.id}": light`;

    if (light.intensity !== undefined && light.intensity < 0) {
      at(`${named} intensity must not be negative`);
    }
    if (light.drop !== undefined && (light.drop <= 0 || light.drop >= ceilingHeight)) {
      at(`${named} drop must be above the floor and below the ceiling`);
    }
    // An offset that puts the fitting outside its own room lights the wrong place, and
    // is far more likely a typo than an intention.
    if (light.offset !== undefined) {
      const centre = {
        x: room.rect.x + room.rect.width / 2 + light.offset.x,
        z: room.rect.z + room.rect.depth / 2 + light.offset.z,
      };
      if (!rectContains(room.rect, centre)) at(`${named} offset puts it outside the room`);
    }
  }

  for (const room of level.rooms) {
    const plate = room.lightSwitch;
    if (plate) {
      const span = wallSpan(room.rect, plate.wall);
      const name = `room "${room.id}": lightSwitch`;
      if (plate.offset < 0 || span.from + plate.offset > span.to) {
        at(`${name} offset falls outside its ${plate.wall} wall`);
      }
    }

    for (const window of room.windows ?? []) {
      findWindowProblems(window, room, level, ceilingHeight).forEach(at);
    }
  }

  for (const door of level.doors) {
    const name = `door "${door.id}"`;
    if (door.width <= 0 || door.height <= 0) at(`${name}: width and height must be positive`);
    if (door.height > ceilingHeight) at(`${name}: height exceeds the ceiling`);

    const [idA, idB] = door.betweenRooms;
    if (idA === idB) at(`${name}: betweenRooms must name two different rooms`);

    const missing = door.betweenRooms.filter((id) => !roomsById.has(id));
    for (const id of missing) at(`${name}: betweenRooms references unknown room "${id}"`);
    if (missing.length > 0 || idA === idB) continue;

    const boundary = sharedBoundary(roomsById.get(idA)!.rect, roomsById.get(idB)!.rect);
    if (!boundary) {
      at(`${name}: rooms "${idA}" and "${idB}" do not share a boundary`);
      continue;
    }

    const along = boundary.axis === 'x' ? door.center.z : door.center.x;
    const across = boundary.axis === 'x' ? door.center.x : door.center.z;
    if (Math.abs(across - boundary.line) > 1e-6) {
      at(`${name}: center is not on the boundary between "${idA}" and "${idB}"`);
    } else if (along - door.width / 2 < boundary.min || along + door.width / 2 > boundary.max) {
      at(`${name}: opening does not fit within the shared boundary`);
    }
  }

  // A grounds level's spawn is checked against the grass instead — see above.
  if (!level.grounds && !level.rooms.some((room) => rectContains(room.rect, level.spawn.position))) {
    at('spawn position must be inside a room');
  }

  for (const object of level.objects) {
    const name = `object "${object.id}"`;
    const room = roomsById.get(object.roomId);
    if (!room) {
      at(`${name}: references unknown room "${object.roomId}"`);
    } else if (!rectContains(room.rect, object.position)) {
      at(`${name}: position is outside room "${object.roomId}"`);
    }
    if (object.size && (object.size.x <= 0 || object.size.y <= 0 || object.size.z <= 0)) {
      at(`${name}: size must be positive on every axis`);
    }
  }

  // One maze, one way out: a second exit would be a second sensor on the same gap.
  if (level.objectives.filter((objective) => objective.kind === 'reach-exit').length > 1) {
    at('has more than one reach-exit objective, and a maze has one exit');
  }

  for (const objective of level.objectives) {
    const name = `objective "${objective.id}"`;

    if (objective.kind === 'reach-exit') {
      if (!level.grounds) at(`${name}: reach-exit needs grounds — a level of rooms has no exit`);
      continue;
    }

    if (level.grounds) {
      at(`${name}: deposit-objects needs a room for its zone, and a grounds level has none`);
      continue;
    }

    for (const id of objective.objectIds) {
      if (!objectIds.has(id)) at(`${name}: references unknown object "${id}"`);
    }
    if (objective.objectIds.length === 0) at(`${name}: objectIds must not be empty`);

    const room = roomsById.get(objective.zone.roomId);
    if (!room) {
      at(`${name}: zone references unknown room "${objective.zone.roomId}"`);
    } else if (!rectContainsRect(room.rect, objective.zone.rect)) {
      at(`${name}: zone rect is not inside room "${objective.zone.roomId}"`);
    }
  }

  for (const [index, step] of (level.steps ?? []).entries()) {
    if (step.size.x <= 0 || step.size.y <= 0 || step.size.z <= 0) {
      at(`step ${index}: size must be positive on every axis`);
    } else if (boxTop(step) > MAX_STEP_HEIGHT) {
      at(`step ${index} rises ${boxTop(step).toFixed(2)}m, above the climbable maximum`);
    }
  }

  return problems;
}

/**
 * Problems that only exist between levels: where an exit leads, and ids that have to be
 * unique across the whole profile rather than within one level.
 *
 * A run is a chain — each level's exit leads to at most one other, and at most one exit
 * leads into each level — so it is a chain that is checked. A level nothing leads to is
 * content that was written and cannot be played, which is a mistake worth failing the
 * build over rather than a shape anyone intends.
 */
function findChainProblems(profile: Profile): string[] {
  const problems: string[] = [];
  const at = (level: LevelSpec, message: string) =>
    problems.push(`level "${level.id}": ${message}`);

  const levelIds = new Set(profile.levels.map((level) => level.id));
  /** Which level already leads into each destination, so a second one can be named. */
  const leadsInto = new Map<string, string>();

  for (const level of profile.levels) {
    const destination = level.exitsTo;
    if (destination === undefined) continue;

    if (destination === level.id) {
      at(level, 'exitsTo names itself — an exit cannot lead back into its own level');
      continue;
    }
    if (!levelIds.has(destination)) {
      at(level, `exitsTo names "${destination}", which no level in this profile declares`);
      continue;
    }
    const already = leadsInto.get(destination);
    if (already !== undefined) {
      at(
        level,
        `exitsTo names "${destination}", which level "${already}" already leads to — a level ` +
          'has one way in, so its return gate would have two places to go',
      );
      continue;
    }
    leadsInto.set(destination, level.id);
  }

  // The first level is where a run starts, so nothing needs to lead to it. Everything
  // else does.
  for (const level of profile.levels.slice(1)) {
    if (!leadsInto.has(level.id)) {
      at(level, 'is unreachable — no level\'s exitsTo names it, and it is not the first level');
    }
  }

  // Objectives are seeded for every level at once and shown for the current one, so an
  // id used twice would be one record for two objectives — the second silently
  // completing when the first does.
  for (const id of findDuplicates(
    profile.levels.flatMap((level) => level.objectives.map((objective) => objective.id)),
  )) {
    problems.push(`duplicate objective id "${id}" across levels`);
  }

  return problems;
}

/** Every problem with a profile, as human-readable strings. Empty means valid. */
export function findProfileProblems(data: unknown): string[] {
  const { profile, problems } = parseProfileData(data);
  if (!profile) return problems;

  const semantic = profile.levels.flatMap(findLevelProblems);
  for (const id of findDuplicates(profile.levels.map((level) => level.id))) {
    semantic.push(`duplicate level id "${id}"`);
  }
  return [...semantic, ...findChainProblems(profile), ...findEndlessProblems(profile)];
}

/**
 * A profile that declares endless continuation must be able to honour it: a size range
 * the generator can build, a maze (not a house) to lead on from, and no authored id that a
 * generated level or objective would collide with.
 */
function findEndlessProblems(profile: Profile): string[] {
  const endless = profile.endless;
  if (!endless) return [];
  const problems: string[] = [];

  const bounds: [string, number][] = [
    ['minCells', endless.minCells],
    ['maxCells', endless.maxCells],
  ];
  let wholeBounds = true;
  for (const [field, cells] of bounds) {
    if (!Number.isInteger(cells)) {
      wholeBounds = false;
      problems.push(`profile.endless.${field} must be a whole number of cells`);
    } else if (cells < MIN_MAZE_CELLS) {
      problems.push(
        `profile.endless.${field} must be at least ${MIN_MAZE_CELLS} cells to be a maze`,
      );
    } else if (cells > MAX_MAZE_CELLS) {
      problems.push(`profile.endless.${field} must be at most ${MAX_MAZE_CELLS} cells`);
    }
  }
  if (wholeBounds && endless.minCells > endless.maxCells) {
    problems.push(
      `profile.endless.minCells (${endless.minCells}) must not exceed maxCells (${endless.maxCells})`,
    );
  }

  for (const level of profile.levels) {
    if (level.exitsTo === undefined && !level.grounds) {
      problems.push(
        `level "${level.id}": the run continues endlessly from here, but this level has no exit ` +
          'to lead on from — only a maze (grounds) has one',
      );
    }
    if (level.id.startsWith(ENDLESS_ID_PREFIX)) {
      problems.push(
        `level id "${level.id}" starts with "${ENDLESS_ID_PREFIX}", which generated mazes use`,
      );
    }
    for (const objective of level.objectives) {
      if (objective.id.startsWith(ENDLESS_ID_PREFIX)) {
        problems.push(
          `objective id "${objective.id}" starts with "${ENDLESS_ID_PREFIX}", which generated mazes use`,
        );
      }
    }
  }
  return problems;
}

/** Parse and validate, or throw with every problem listed. */
export function parseProfile(data: unknown): Profile {
  const problems = findProfileProblems(data);
  if (problems.length > 0) {
    throw new Error(`Invalid profile:\n- ${problems.join('\n- ')}`);
  }
  // findProfileProblems returning [] implies parseProfileData succeeded.
  return parseProfileData(data).profile!;
}
